import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { createNotification as createNotificationRequest } from "../api/notification.api";
import type { CreateNotificationRequest } from "../types/notification.types";

interface UseCreateNotificationReturn {
  loading: boolean;
  error: string | null;
  success: boolean;
  createNotification: (data: CreateNotificationRequest) => Promise<void>;
  reset: () => void;
}

export const useCreateNotification = (): UseCreateNotificationReturn => {
  const { t } = useTranslation();
  const { handleResult } = useResultHandler();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const createNotification = useCallback(
    async (data: CreateNotificationRequest) => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const response = await createNotificationRequest(data);
        const isSuccess = handleResult(response, {
          successMessage: t("notification.notification_created_successfully"),
          showErrorToast: false,
        });

        if (isSuccess) {
          setSuccess(true);
          return;
        }

        setError(response.user_message || response.message || "Failed to create notification");
      } catch (caughtError: unknown) {
        setError(getApiErrorMessage(caughtError, "Failed to create notification"));
      } finally {
        setLoading(false);
      }
    },
    [handleResult, t],
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return { loading, error, success, createNotification, reset };
};
